/* ──────────────────────────────────────────────────────────────────────
   v26 Cart Bag Trigger

   Intercepts clicks on any v26 bag/cart link (links with
   `data-v26-bag-trigger`, or fallback selectors used across v26 nav
   sections) and opens the slide-in cart drawer instead of navigating
   to /cart. Native navigation is preserved when:
     - The user is already on /cart (no drawer needed)
     - The user holds modifier keys (cmd/ctrl/shift/alt) or middle-clicks
     - The drawer isn't on the page (e.g. cart_type = 'page' in settings)

   Auto-syncs every bag link's "(N)" item count when the Cart store fires.
   ────────────────────────────────────────────────────────────────────── */
import Cart from "cart-store";

(function () {
  "use strict";

  const SELECTORS = [
    "[data-v26-bag-trigger]",
    ".v26-hero__nav-right a[href='/cart']",
    ".v26-ph__nav-right a[href='/cart']",
    ".v26-sh__nav-right a[href='/cart']",
    ".v26-hiw__nav-right a[href='/cart']",
    ".v26-rh__nav-right a[href='/cart']",
    ".v26cartpage__nav-right a[href='/cart']",
    "a.v26-cart[href='/cart']",
    ".v26-mm a[href='/cart']"
  ].join(",");

  function findDrawer() {
    // Parent theme renders the drawer as `<modal-dialog id="modal-cart">`
    return document.querySelector(".modal--cart");
  }

  function isModifiedClick(e) {
    return e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1;
  }

  function onClick(e) {
    const link = e.target.closest(SELECTORS);
    if (!link) return;
    if (isModifiedClick(e)) return;

    // If we're already on /cart, let the click fall through (no-op anyway).
    if (window.location && window.location.pathname && window.location.pathname.replace(/\/$/, "") === "/cart") return;

    const drawer = findDrawer();
    if (!drawer || typeof drawer.open !== "function") return; // graceful fallback

    e.preventDefault();
    try {
      if (!drawer.shown) drawer.open();
    } catch (err) {
      // If open() throws for any reason, let the link navigate normally.
      window.location.href = link.getAttribute("href") || "/cart";
    }
  }

  // Update every bag link's "Bag (N)" label + mobile-badge count when the
  // cart-store fires. Keeps the desktop "(N)" parenthetical in sync without
  // a page reload after an AJAX add/remove.
  function updateBagLabels(itemCount) {
    document.querySelectorAll("[data-v26-bag-trigger]").forEach(function (a) {
      // Replace "Bag (N)" — keep any leading "Bag" label intact.
      const text = a.textContent || "";
      const match = text.match(/^(\s*Bag\s*\()(\d+)(\)\s*)$/i);
      if (match) {
        a.textContent = match[1] + itemCount + match[3];
      }
      // Mobile menu "Bag (N)" count — when the link wraps an icon + label
      const mmCount = a.querySelector(".v26-mm__bag-count");
      if (mmCount) mmCount.textContent = "(" + itemCount + ")";
      // Mobile icon badge (the small pill above the bag icon)
      const badge = a.querySelector(".v26-cart__badge");
      if (badge) {
        if (itemCount > 0) {
          badge.textContent = String(itemCount);
          badge.removeAttribute("hidden");
        } else {
          badge.setAttribute("hidden", "");
        }
      }
    });
  }

  function init() {
    document.addEventListener("click", onClick, true);

    // Initial sync (Cart store hydrates from #cart-data on load).
    try {
      const initial = Cart.getState().cart;
      if (initial) updateBagLabels(initial.item_count);
      Cart.subscribe(function (state) {
        if (state && state.cart) updateBagLabels(state.cart.item_count);
      });
    } catch (err) {
      // Cart store not ready yet — try again on next tick.
      setTimeout(init, 200);
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
